import * as Sentry from '@sentry/react';

export type RuntimeConfig = {
  apiBaseUrl?: string;

  webSocketUrl?: string;

  cloudUrl?: string;

  standinDeployment?: boolean;
};

export const RUNTIME_CONFIG_GLOBAL = '__TC_RUNTIME_CONFIG__';

type RuntimeConfigHost = { __TC_RUNTIME_CONFIG__?: unknown };

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;

const LOOPBACK_HOSTNAMES = ['localhost', '127.0.0.1', '::1', '[::1]'];

const PRIVATE_IPV4 = /^(?:10\.|192\.168\.|169\.254\.|172\.(?:1[6-9]|2\d|3[01])\.)/;

const UNIQUE_LOCAL_IPV6 = /^\[?f[cd][0-9a-f]{2}:/;

const SECURE_SCHEMES = ['https:', 'wss:'];

const PLAINTEXT_SCHEMES = ['http:', 'ws:'];

export function isLocalNetworkHostname(hostname: string): boolean {
  const name = hostname.toLowerCase();
  return (
    LOOPBACK_HOSTNAMES.includes(name) || PRIVATE_IPV4.test(name) || UNIQUE_LOCAL_IPV6.test(name)
  );
}

export function isAcceptableRuntimeDestination(value: string): boolean {
  try {
    const { protocol, hostname } = new URL(value);
    if (SECURE_SCHEMES.includes(protocol)) return true;
    if (!PLAINTEXT_SCHEMES.includes(protocol)) return false;
    return isLocalNetworkHostname(hostname);
  } catch {
    return false;
  }
}

const readDestination = (value: unknown, field: string): string | undefined => {
  const candidate = readString(value);
  if (!candidate) return undefined;
  if (isAcceptableRuntimeDestination(candidate)) return candidate;

  Sentry.logger.error(
    Sentry.logger
      .fmt`[RuntimeConfig] Refusing ${field} from the runtime config document: ${candidate}`
  );
  return undefined;
};

export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

  const source = raw as Record<string, unknown>;
  const config: RuntimeConfig = {};

  const apiBaseUrl = readDestination(source.apiBaseUrl, 'apiBaseUrl');
  if (apiBaseUrl) config.apiBaseUrl = apiBaseUrl;

  const webSocketUrl = readDestination(source.webSocketUrl, 'webSocketUrl');
  if (webSocketUrl) config.webSocketUrl = webSocketUrl;

  const cloudUrl = readDestination(source.cloudUrl, 'cloudUrl');
  if (cloudUrl) config.cloudUrl = cloudUrl;

  if (source.standinDeployment === true || source.standinDeployment === 'true') {
    config.standinDeployment = true;
  } else if (source.standinDeployment === false || source.standinDeployment === 'false') {
    config.standinDeployment = false;
  }

  return config;
}

export function readRuntimeConfig(host?: RuntimeConfigHost): RuntimeConfig {
  try {
    const source: RuntimeConfigHost | undefined =
      host ??
      (typeof window === 'undefined' ? undefined : (window as unknown as RuntimeConfigHost));
    return parseRuntimeConfig(source?.[RUNTIME_CONFIG_GLOBAL]);
  } catch {
    return {};
  }
}

export default readRuntimeConfig;
